import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Button, Divider, Header, Icon, Modal, Segment, Table, Label } from 'semantic-ui-react'
import ClientPageContext from './ClientPageContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../../../common/repository/config';
import LocalStorage from '../../../../lib/LocalStorage';
import FileLabel from '../../../common/FileLabel';

interface PromptFormat {
    _id: string;
    prompt: string | null;
    format: string | null;
    letter_head: boolean;
    treatment_type: string;
    created_on: string;
    modified_on: string;
    client: string;
    client_name: string;
    company: string | null;
    company_name: string | null;
    case_type: string;
    case_type_name: string;
}

const AddPrompt = () => {
    const ctx = useContext(ClientPageContext);

    const [items, setItems] = useState<PromptFormat[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState<PromptFormat | null>(null);
    const [formatModalOpen, setFormatModalOpen] = useState(false);

    const isCurrentClientTpa = useMemo(() => {
        return ctx.client?.getType().toLowerCase() === 'tpa';
    }, [ctx.client]);

    const fetchFormatsAndPrompts = async () => {
        try {
            setLoading(true);

            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN) || "";

            const res = await axios.post(
                `${config.apiBaseURL}/promptAndFormat/getPrompts`,
                {
                    clientId: ctx.client?.getId(),
                    clientType: ctx.client?.getType(),
                },
                {
                    headers: {
                        "x-access-token": token,
                    },
                }
            );

            const responseItems = res.data?.data?.items;

            if (Array.isArray(responseItems)) {
                setItems(responseItems);
            } else {
                setItems([]);
            }
        } catch (error) {
            setItems([]);
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Failed to fetch formats"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (ctx.client?.getId()) {
            fetchFormatsAndPrompts();
        }
    }, [ctx.client]);

    const handleViewFormat = (item: PromptFormat) => {
        if (!item.format) {
            toast.info("No format available for this prompt");
            return;
        }

        setSelectedFormat(item);
        setFormatModalOpen(true);
    };

    const handleCloseFormat = () => {
        setFormatModalOpen(false);
        setSelectedFormat(null);
    };

    const formatDate = (date: string) => {
        if (!date) return "-";

        return new Date(date).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <Segment padded="very" color="teal" raised>
            <Header as="h2" color="teal" textAlign="center">
                <Icon name="file alternate outline" />
                <Header.Content>Added Prompt</Header.Content>
            </Header>

            <Divider />

            <Header as="h3">
                Existing Prompts for {ctx.client?.getName() || 'Current Client'}{" "}
                {isCurrentClientTpa ? '(TPA)' : '(Company)'}
            </Header>

            {loading ? (
                <Segment textAlign="center" basic>
                    <Icon name="spinner" loading size="large" />
                    <Header as="h4">Loading prompts...</Header>
                </Segment>
            ) : items.length === 0 ? (
                <Segment placeholder textAlign="center">
                    <Header icon>
                        <Icon name="file outline" />
                        No prompts or formats found
                    </Header>
                    <p>
                        There are no prompts or formats configured for this client.
                    </p>
                </Segment>
            ) : (
                <Table celled structured compact="very" selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>#</Table.HeaderCell>
                            <Table.HeaderCell>Client</Table.HeaderCell>

                            {isCurrentClientTpa && (
                                <Table.HeaderCell>Company</Table.HeaderCell>
                            )}

                            <Table.HeaderCell>Case Type</Table.HeaderCell>
                            <Table.HeaderCell>Treatment Type</Table.HeaderCell>
                            <Table.HeaderCell>Letter Head</Table.HeaderCell>
                            <Table.HeaderCell>Prompt</Table.HeaderCell>
                            <Table.HeaderCell>Format</Table.HeaderCell>
                            <Table.HeaderCell>Created On</Table.HeaderCell>
                            <Table.HeaderCell>Modified On</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {items.map((item, index) => (
                            <Table.Row key={item._id}>
                                <Table.Cell>{index + 1}</Table.Cell>

                                <Table.Cell>
                                    {item.client_name || "-"}
                                </Table.Cell>

                                {isCurrentClientTpa && (
                                    <Table.Cell>
                                        {item.company_name || "-"}
                                    </Table.Cell>
                                )}

                                <Table.Cell>
                                    {item.case_type_name || "-"}
                                </Table.Cell>

                                <Table.Cell>
                                    {item.treatment_type || "-"}
                                </Table.Cell>

                                <Table.Cell>
                                    {item.letter_head ? (
                                        <Label color="green">
                                            Yes
                                        </Label>
                                    ) : (
                                        <Label basic>
                                            No
                                        </Label>
                                    )}
                                </Table.Cell>

                                <Table.Cell>
                                    {item.prompt ? (
                                        <FileLabel
                                            title="View Prompt"
                                            link={`${config.frontEndURL}/${item.prompt}`}
                                        />
                                    ) : (
                                        <span>N/A</span>
                                    )}
                                </Table.Cell>

                                <Table.Cell>
                                    {item.format ? (
                                        <Button
                                            size="small"
                                            color="teal"
                                            onClick={() => handleViewFormat(item)}
                                        >
                                            <Icon name="eye" />
                                            View Format
                                        </Button>
                                    ) : (
                                        <Label basic>
                                            No Format
                                        </Label>
                                    )}
                                </Table.Cell>

                                <Table.Cell>
                                    {formatDate(item.created_on)}
                                </Table.Cell>

                                <Table.Cell>
                                    {formatDate(item.modified_on)}
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}

            <Modal
                open={formatModalOpen}
                onClose={handleCloseFormat}
                size="fullscreen"
            >
                <Modal.Header>
                    <Icon name="file alternate outline" />
                    {selectedFormat?.case_type_name || "Format"}
                </Modal.Header>

                <Modal.Content
                    scrolling
                    style={{
                        padding: 0,
                        height: "calc(100vh - 120px)"
                    }}
                >
                    {selectedFormat?.format ? (
                        <iframe
                            title="Report Format"
                            srcDoc={selectedFormat.format}
                            style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                display: "block"
                            }}
                        />
                    ) : (
                        <Segment placeholder textAlign="center">
                            <Header icon>
                                <Icon name="file outline" />
                                No format available
                            </Header>
                        </Segment>
                    )}
                </Modal.Content>

                <Modal.Actions>
                    <Button onClick={handleCloseFormat}>
                        <Icon name="close" />
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </Segment>
    );
};

export default AddPrompt;